import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class ResultsService {
  private baseUrl = '/api/interactions/';
  private cache = new Map<string, Observable<any>>();

  constructor(private http: HttpClient) {}

  private getCached<T>(key: string, request: Observable<T>): Observable<T> {
    if (!this.cache.has(key)) {
      this.cache.set(key, request.pipe(shareReplay(1)));
    }
    return this.cache.get(key)!;
  }

  clearCache(): void {
    this.cache.clear();
  }

  getInteractionSnapshot(interactionId: string, resultId: string): Observable<any> {
    const key = `snapshot-${interactionId}-${resultId}`;
    return this.getCached(key, this.http.get(
      `${this.baseUrl}${interactionId}/results/${resultId}/interaction-snapshot`
    ));
  }

  getExecutionDetails(interactionId: string, resultId: string): Observable<any> {
    const key = `details-${interactionId}-${resultId}`;
    return this.getCached(key, this.http.get(
      `${this.baseUrl}${interactionId}/results/${resultId}/execution-details`
    ));
  }

  getResultOutputs(
    interactionId: string, 
    resultId: string, 
    archiveId: string
  ): Observable<any> {
    const key = `outputs-${interactionId}-${resultId}-${archiveId}`;
    return this.getCached(key, this.http.get(
      `${this.baseUrl}${interactionId}/results/${resultId}/outputs/${archiveId}`
    ));
  }

  getResultLogs(interactionId: string, resultId: string): Observable<any> {
    return this.http.get(
      `${this.baseUrl}${interactionId}/results/${resultId}/logs`
    );
  }

  listResults(interactionId: string): Observable<any> {
    return this.http.get(`${this.baseUrl}${interactionId}/results`);
  }

  createResult(interactionId: string, data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}${interactionId}/results`, data);
  }

  deleteResult(interactionId: string, resultId: string): Observable<any> {
    return this.http.delete(`${this.baseUrl}${interactionId}/results/${resultId}`);
  }

  cancelExecution(interactionId: string, resultId: string): Observable<any> {
    return this.http.post(`${this.baseUrl}${interactionId}/results/${resultId}/cancel`, {});
  }

  retryFailedArchives(interactionId: string, resultId: string): Observable<any> {
    return this.http.post(`${this.baseUrl}${interactionId}/results/${resultId}/retry-failed`, {});
  }

  sendGuardFeedback(interactionId: string, resultId: string, archiveId: string, userLabel: 'safe' | 'unsafe'): Observable<any> {
    return this.http.post(
      `${this.baseUrl}${interactionId}/results/${resultId}/outputs/${archiveId}/feedback`,
      { user_label: userLabel }
    );
  }

  reprocessWithoutGuard(interactionId: string, resultId: string, archiveId: string): Observable<any> {
    return this.http.post(
      `${this.baseUrl}${interactionId}/results/${resultId}/outputs/${archiveId}/reprocess`,
      {}
    );
  }

  reportAsUnsafe(interactionId: string, resultId: string, archiveId: string, target: 'document' | 'prompt' = 'document'): Observable<any> {
    return this.http.post(
      `${this.baseUrl}${interactionId}/results/${resultId}/outputs/${archiveId}/report-unsafe`,
      { target }
    );
  }

  getAllResultsAsJson(interactionId: string, resultId: string): Observable<any> {
    return this.http.get(`${this.baseUrl}${interactionId}/results/${resultId}/all-outputs`);
  }

  getCompareData(interactionId: string, resultId: string, referenceResultId?: string): Observable<any> {
    let url = `${this.baseUrl}${interactionId}/results/${resultId}/compare-data`;
    if (referenceResultId) {
      url += `?reference_result_id=${referenceResultId}`;
    }
    const key = `compare-${interactionId}-${resultId}-${referenceResultId || ''}`;
    return this.getCached(key, this.http.get(url));
  }

  async downloadAllResultsAsZip(interactionId: string, resultId: string, zipName: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.getAllResultsAsJson(interactionId, resultId).subscribe({
        next: async (response: any) => {
          if (response.all_results) {
            try {
              const JSZip = (await import('jszip')).default;
              const zip = new JSZip();
              
              Object.keys(response.all_results).forEach(fileName => {
                const data = response.all_results[fileName];
                const cleanFileName = fileName.replace(/\.[^/.]+$/, "") + '.json';
                zip.file(cleanFileName, JSON.stringify(data, null, 2));
              });
              
              const content = await zip.generateAsync({ type: 'blob' });
              const url = window.URL.createObjectURL(content);
              
              const a = document.createElement('a');
              a.href = url;
              const cleanZipName = zipName.replace(/\s+/g, '_');
              a.download = `${cleanZipName}_all_results.zip`;
              document.body.appendChild(a);
              a.click();
              window.URL.revokeObjectURL(url);
              document.body.removeChild(a);
              resolve();
            } catch (err) {
              reject(err);
            }
          } else {
            resolve();
          }
        },
        error: (error: any) => {
          reject(error);
        }
      });
    });
  }

  async downloadAllOcrAsZip(interactionId: string, resultId: string, zipName: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.getAllResultsAsJson(interactionId, resultId).subscribe({
        next: async (response: any) => {
          const allOcrResults = response.all_ocr_results ?? {};

          if (Object.keys(allOcrResults).length === 0) {
            reject(new Error('Nenhum resultado de OCR disponível.'));
            return;
          }

          try {
            const JSZip = (await import('jszip')).default;
            const zip = new JSZip();

            Object.keys(allOcrResults).forEach(fileName => {
              const data = allOcrResults[fileName];
              const cleanFileName = fileName.replace(/\.[^/.]+$/, '') + '.json';
              const content = typeof data === 'string' ? data : JSON.stringify(data, null, 2);
              zip.file(cleanFileName, content);
            });

            const zipContent = await zip.generateAsync({type: 'blob'});
            const url = window.URL.createObjectURL(zipContent);
            const anchor = document.createElement('a');
            
            anchor.href = url;
            const cleanZipName = zipName.trim().replace(/\s+/g, '_').replace(/[<>:"/\\|?*]/g, '_');

            anchor.download = `${cleanZipName}_ocr.zip`;
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
            window.URL.revokeObjectURL(url);
            resolve();
          } catch (error) {
            reject(error);
          }
        },

        error: (error: any) => {
          reject(error);
        }
      });
    });
  }
}
